import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  TextField,
  ToggleButton,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";

import {
  AI_Entity,
  Dashboard_Entity,
  Entity,
  Entity_Label,
  FolderType,
  ModulePath,
  Severity,
} from "../../../constant/ApplicationVariables";
import {
  HomeOutlined,
  MenuOpen,
  MoreVertOutlined,
  Settings,
} from "@mui/icons-material";
import ViewListOutlined from "@mui/icons-material/ViewListOutlined";
import { LightTooltip } from "../../styled/CustomComponent";
import useErrorHandler from "../../../api/errorHandler/useErrorHandler";
import { useApplicationContext } from "../../../context/ApplicationContext";

import { font } from "../../../styles/font";
import { useLocation, useNavigate } from "react-router-dom";

import { KeyboardShortcuts } from "../../../constant/KeyboardKeys";
import ActiveProjectCard from "./ActiveProjectCard";
import ShortcutText from "../../common/ShortcutText";
import { useTranslation } from "react-i18next";
import { lazy, memo, useEffect, useState } from "react";
import PlanningSidebar from "./PlanningSidebar";
import { Suspense } from "react";
import FallbackUILoader from "../../loader/FallbackUILoader";
import { useCallback } from "react";
import SettingsSidebar from "./SettingsSidebar";
import ActiveOrganisationCard from "./ActiveOrganisationCard";
import SuperAdminSidebar from "./SuperAdminSidebar";

const DashboardSidebar = lazy(() => import("./DashboardSidebar"));
const FoldersSidebar = lazy(() => import("./FoldersSidebar"));
const ConfigurationSidebar = lazy(() => import("./ConfigurationSidebar"));

const ApplicationSidebar = memo(() => {
  const {
    toggleFolderSidebar,
    setToggleFolderSidebar,
    setActiveProject,
    setFolderStructureList,
    setUserStoryList,
    setUseCaseList,
    setTestCaseList,
    setDashboardReportList,
    setCurrentFolderData,
    setOpenCreateStoryDialog,
    setOpenEditStoryDialog,
    setOpenDeleteStoryDialog,
    setOpenCreateUseCaseDialog,
    setOpenEditUseCaseDialog,
    setOpenDeleteUseCaseDialog,
    activeProject,
    currentFolderData,
    setActiveConfiguration,
    setToggleChatWindow,
  } = useApplicationContext();
  const { t } = useTranslation();

  const theme = useTheme();
  const navigate = useNavigate();
  const pathName = useLocation()?.pathname;

  // State for More Options Menu (Moved from ActiveProjectCard)
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  
  // State to track if editor is maximized
  const [isEditorMaximized, setIsEditorMaximized] = useState(false);

  // Function to handle editor maximize from EditTestCaseComponent
  const handleEditorMaximize = useCallback((isMaximized) => {
    setIsEditorMaximized(isMaximized);
    if (isMaximized) {
      setToggleFolderSidebar(false);
    }
  }, [setToggleFolderSidebar]);

  const handleCardComponent = () => {
    switch (pathName) {
      case ModulePath.settings:
        return <ActiveOrganisationCard />;
      case ModulePath.requirements:
      case ModulePath.useCase:
      case ModulePath.testCase:
      case ModulePath.planning:
      case ModulePath.execution:
      case ModulePath.dashboard:
      case ModulePath.configuration:
        return <ActiveProjectCard />;
      case ModulePath.superAdmin:
        return null; // No card for super admin
      default:
        return null;
    }
  };

  const handleSidebar = () => {
    switch (pathName) {
      case ModulePath.requirements:
      case ModulePath.useCase:
      case ModulePath.testCase:
      case ModulePath.planning:
      case ModulePath.execution:
        return <FoldersSidebar />;
      case ModulePath.dashboard:
        return <DashboardSidebar />;
      case ModulePath.configuration:
        return <ConfigurationSidebar />;
      case ModulePath.settings:
        return <SettingsSidebar />;
      case ModulePath.superAdmin:
        return <SuperAdminSidebar />;
      default:
        return "";
    }
  };

  const handleHomeClick = useCallback(() => {
    navigate(ModulePath.projects);
    setActiveProject(null);
    localStorage.removeItem("activeProject");

    setFolderStructureList([]);
    setUserStoryList([]);
    setUseCaseList([]);
    setTestCaseList([]);

    setCurrentFolderData(null);

    setToggleFolderSidebar(true);
    setToggleChatWindow(false);

    setOpenEditStoryDialog(false);
    setOpenCreateStoryDialog(false);
    setOpenDeleteStoryDialog(false);
    setOpenEditUseCaseDialog(false);
    setOpenCreateUseCaseDialog(false);
    setOpenDeleteUseCaseDialog(false);
  }, [
    navigate,
    setActiveProject,
    setFolderStructureList,
    setUserStoryList,
    setUseCaseList,
    setTestCaseList,
    setCurrentFolderData,
    setOpenCreateStoryDialog,
    setOpenEditStoryDialog,
    setOpenDeleteStoryDialog,
    setOpenCreateUseCaseDialog,
    setOpenEditUseCaseDialog,
    setOpenDeleteUseCaseDialog,
    setToggleFolderSidebar,
    setToggleChatWindow,
  ]);

  // Add these constants at the top
const SIDEBAR_WIDTHS = {
  expanded: 180,
  collapsed: 64,
};

const SIDEBAR_STATE_KEY = 'sidebar_expanded_state';

// Inside the component, add this useEffect
useEffect(() => {
  // Load saved state from localStorage
  const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
  if (savedState !== null) {
    const parsedState = JSON.parse(savedState);
    if (parsedState !== toggleFolderSidebar) {
      setToggleFolderSidebar(parsedState);
    }
  }
}, []); // Only run on mount

// Update localStorage whenever toggleFolderSidebar changes
useEffect(() => {
  localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(toggleFolderSidebar));
}, [toggleFolderSidebar]);

  const handleToggleMoreMenu = (event) => {
    if (moreMenuAnchorEl) {
      setMoreMenuAnchorEl(null);
    } else {
      setMoreMenuAnchorEl(event.currentTarget);
    }
  };

  const handleViewAllItems = useCallback(() => {
    setCurrentFolderData(null);
  }, [setCurrentFolderData]);

  const handleClickMoreItem = (itemValue) => {
    setActiveConfiguration({ label: itemValue, value: itemValue });
    setMoreMenuAnchorEl(null);
    navigate(ModulePath.configuration);
  };

  // If editor is maximized, hide sidebar
  if (isEditorMaximized) {
    return (
      <Box
        sx={{
          width: 0,
          height: "100%",
          overflow: "hidden",
          transition: "width 0.3s ease",
        }}
      />
    );
  }

  const isSuperAdmin = pathName === ModulePath.superAdmin;

  return (
    <Box
      className="flex flex-col gap-0 w-full h-full relative overflow-hidden"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Custom Sidebar Toggle Button */}
      <LightTooltip
        title={
          <Box className="flex items-center gap-2">
            <Typography 
              fontSize={font.size.fs_5} 
              color={theme.palette.text.primary}
            >
              {t(
                toggleFolderSidebar
                  ? "header.hideSidebarButton"
                  : "header.showSidebarButton"
              )}
            </Typography>
            <ShortcutText
              keyboardShortcutText={KeyboardShortcuts.header.toggleSidebar}
              textColor={theme.palette.text.secondary}
            />
          </Box>
        }
        placement="right"
        arrow
      >
        <Box
          className="absolute z-10"
          sx={{
            top: 45,
            left: "100%",
            transform: "translateX(-50%)",
          }}
        >
          <ToggleButton
            value="check"
            size="small"
            selected={!toggleFolderSidebar}
            onChange={() =>
              setToggleFolderSidebar((prevSelected) => !prevSelected)
            }
            sx={{
              borderRadius: "50%",
              padding: 0.5,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.primary.main,
              "&:hover": { 
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-selected": { 
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuOpen
              sx={{ 
                transform: !toggleFolderSidebar && "rotate(180deg)",
                color: theme.palette.primary.main
              }}
            />
          </ToggleButton>
        </Box>
      </LightTooltip>

      {/* COLLAPSED SIDEBAR: toggleFolderSidebar = false */}
      {!toggleFolderSidebar ? (
        <Box className="flex flex-col items-center gap-0 w-full h-full overflow-hidden">
          {/* header container */}
          <Box className="flex flex-col items-center gap-4 px-4 py-4">
            {/* Home Button - Hidden for Super Admin */}
            {!isSuperAdmin && (
              <Box className="flex flex-col gap-4 px-0 py-0">
                <LightTooltip
                  title={t("header.homeButtonText")}
                  placement="right"
                  arrow
                >
                  <ToggleButton
                    value="check"
                    size="small"
                    color="primary"
                    selected={true}
                    onChange={handleHomeClick}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    <HomeOutlined />
                  </ToggleButton>
                </LightTooltip>
              </Box>
            )}

            {/* project card - Hidden for Super Admin */}
            {!isSuperAdmin && handleCardComponent() && (
              <Box className="flex items-center gap-0 overflow-hidden min-h-fit">
                {handleCardComponent()}
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        /* EXPANDED SIDEBAR: toggleFolderSidebar = true */
        <Box className="flex flex-col gap-0 w-full h-full overflow-hidden">
          {/* HEADER CONTAINER - Hidden for Super Admin */}
          {!isSuperAdmin && (
            <Box className="flex flex-col gap-4 px-4 py-2 pb-1">
              {/* Row 1: Home Link | Spacer | Action Icons */}
              <Box className="flex items-center justify-between gap-2 px-0 py-0">
                {/* Home Link (Icon + Text) - Left Side */}
                <Box
                  className="flex items-center gap-1 cursor-pointer rounded-sm px-2 py-1"
                  onClick={handleHomeClick}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: theme.shape.borderRadius,
                    "&:hover": { 
                      backgroundColor: theme.palette.primary.dark,
                      opacity: 0.9,
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <HomeOutlined
                    fontSize="small"
                    sx={{ color: theme.palette.secondary.main }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ color: theme.palette.secondary.main }}
                  >
                    {t("header.homeButtonText")}
                  </Typography>
                </Box>

                {/* Action Icons - Right Side */}
                <Box className="flex items-center gap-1">
                  {/* More Options Icon */}
                  {pathName !== ModulePath.configuration && activeProject && (
                    <IconButton
                      onClick={handleToggleMoreMenu}
                      size="small"
                      aria-label="More options"
                      sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MoreVertOutlined sx={{ fontSize: font.size.fs_3 }} />
                    </IconButton>
                  )}

                  {/* View All Items Icon */}
                  {(pathName === ModulePath.testCase ||
                    pathName === ModulePath.useCase ||
                    pathName === ModulePath.requirements) &&
                    activeProject && (
                      <IconButton
                        size="small"
                        color={!currentFolderData ? "secondary" : "default"}
                        onClick={handleViewAllItems}
                        aria-label="View all items"
                        sx={{
                          color: !currentFolderData 
                            ? theme.palette.primary.main 
                            : theme.palette.text.secondary,
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <ViewListOutlined sx={{ fontSize: font.size.fs_3 }} />
                      </IconButton>
                    )}
                </Box>
              </Box>
            </Box>
          )}

          {/* folder structure */}
          <Box
            className="flex-1 flex flex-col px-0 py-0 gap-0 overflow-hidden"
            sx={{
              backgroundColor: isSuperAdmin 
                ? theme.palette.background.default 
                : theme.palette.background.default,
            }}
          >
            <Suspense fallback={<FallbackUILoader />}>
              {handleSidebar()}
            </Suspense>
          </Box>
        </Box>
      )}

      {/* Menu for More Options */}
      <Menu
        open={Boolean(moreMenuAnchorEl)}
        anchorEl={moreMenuAnchorEl}
        onClose={handleToggleMoreMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ 
          sx: { 
            minWidth: 140,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[8],
          } 
        }}
      >
        <MenuItem 
          onClick={() => handleClickMoreItem("Project")}
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          Project
        </MenuItem>
        <MenuItem 
          onClick={() => handleClickMoreItem("Labels")}
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          Labels
        </MenuItem>
        <MenuItem 
          onClick={() => handleClickMoreItem("Activity")}
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          Activity
        </MenuItem>
        <MenuItem 
          onClick={() => handleClickMoreItem("Boundary Configuration")}
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          Boundary Configuration
        </MenuItem>
      </Menu>
    </Box>
  );
});

export default ApplicationSidebar;